/**
 * Admin-only override for the hover-toggle delay used by admin/progress-builder
 * surfaces.
 *
 * Gameplay settings remain limited to 1s / 2s (see `HoverToggleDuration`).
 * Admin tooling needs a 0s option for verification efficiency without leaking
 * the option into normal settings or save data, so the override is stored
 * separately in admin runtime preferences.
 *
 * - `null` means "fall back to the player's gameplay hover-toggle duration".
 * - `0`, `1000`, `2000` are admin-only absolute durations in milliseconds.
 */

export const ADMIN_HOVER_TOGGLE_DELAY_MS_OPTIONS: readonly number[] = [
  0, 1000, 2000,
];

export type AdminHoverToggleDelayMsOverride = number | null;

export function isAdminHoverToggleDelayMsOption(
  value: unknown,
): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    ADMIN_HOVER_TOGGLE_DELAY_MS_OPTIONS.includes(value)
  );
}

export function normalizeAdminHoverToggleDelayMsOverride(
  value: unknown,
): AdminHoverToggleDelayMsOverride {
  return isAdminHoverToggleDelayMsOption(value) ? value : null;
}

export function resolveAdminHoverToggleDurationMs(
  override: AdminHoverToggleDelayMsOverride,
  gameplayDurationMs: number,
): number {
  if (override !== null) {
    return override;
  }
  return Math.max(0, Math.trunc(gameplayDurationMs));
}

export function formatAdminHoverToggleDelayMs(value: number): string {
  return `${Math.trunc(value / 1000)}s`;
}
